import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * dm-magnet — the photo step on a product tagged photo-upload (snippets/dm-magnet-fields.liquid).
 *
 * It owns validation and the post for that one form, because no shared add-to-cart path can be
 * trusted with a file:
 *   · product-form.js switches to JSON when the pup's room has a pick and keeps string properties
 *     only (lines 458-479), so the photo would vanish. dm-room renders nothing on this product.
 *   · sticky-add-to-cart.js builds its own id + quantity body (lines 400-402), so it skips every
 *     field, and on the product-standard template that bar is the only visible button (the inline
 *     form is hidden, product-information.liquid:1613). dm-magnet-fields swaps the two by CSS.
 *   · dm-standard-form.js posts JSON only, so that form does not mount on this product.
 *
 * The theme's on:submit and on:click handlers run on document in the capture phase
 * (component.js), so the only earlier place to step in is window, also in capture. The handlers
 * below act only on this form, which exists only on a tagged product, so other products never
 * reach this code.
 *
 * The body is a FormData with no Content-Type header: the browser then writes the multipart
 * boundary itself. Shopify stores the file and returns its URL as the Photo property.
 */

/* Shopify's docs state no limit for a file line item property; 20 MB is the figure reported by
   community and third-party sources, so it is enforced here before a long upload can fail. */
const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
/* Some browsers report an empty type for a HEIC file, so the name also counts. */
const PHOTO_NAME = /\.(jpe?g|png|heic)$/i;

const WRONG_TYPE_MESSAGE = 'Please add a JPG or PNG photo.';
const TOO_LARGE_MESSAGE = 'Please add a photo under 20 MB.';
// One cart-error fallback across the buy forms (dm-standard-form.js, dm-personalizer.js).
const CART_ERROR_FALLBACK = 'Unable to add this to your cart right now. Please try again.';
const ADDED_LABEL = 'Added to cart';
// Same hold as the inline CTAs (motion 206:6962).
const ADDED_HOLD_MS = 2000;

class DmMagnetFields extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {Record<string, string>} Drawn copy of each inline error, read from the markup. */
  #messages = {};

  /** The accepted photo, kept so a cancelled or rejected pick cannot lose it. @type {File | null} */
  #photo = null;

  #thumbUrl = '';
  #submitting = false;
  #addedTimer = 0;

  connectedCallback() {
    this.#form = this.closest('form');
    if (!this.#form) return;

    // Inline errors replace the browser's bubbles. `required` stays on every control, so
    // form.checkValidity() (AddToCartComponent) and the no-JS post stay honest.
    this.#form.noValidate = true;

    for (const note of this.querySelectorAll('[data-dm-magnet-error]')) {
      const key = /** @type {HTMLElement} */ (note).dataset.dmMagnetError || '';
      this.#messages[key] = note.textContent?.trim() || '';
    }

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    window.addEventListener('click', this.#onWindowClick, { capture: true, signal });
    window.addEventListener('submit', this.#onWindowSubmit, { capture: true, signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    // CSS already swaps the sticky bar for the inline form; this covers a browser without :has().
    document.querySelector('sticky-add-to-cart')?.remove();
    const inlineForm = this.closest('.buy-buttons-block');
    if (inlineForm instanceof HTMLElement) inlineForm.style.display = 'block';

    // A browser can bring the file back on reload or back navigation.
    if (this.#file?.files?.length) this.#onPhotoPicked();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    window.clearTimeout(this.#addedTimer);
    this.#revokeThumb();
  }

  // ------------------------------------------------------------------ controls

  get #name() {
    const element = this.querySelector('[data-dm-magnet-name]');
    return element instanceof HTMLInputElement ? element : null;
  }

  get #file() {
    const element = this.querySelector('[data-dm-magnet-file]');
    return element instanceof HTMLInputElement ? element : null;
  }

  get #consent() {
    const element = this.querySelector('[data-dm-magnet-consent]');
    return element instanceof HTMLInputElement ? element : null;
  }

  /** The dm button rendered by dm-standard-form, else the theme's add-to-cart button. */
  get #cta() {
    const element = this.#form?.querySelector('[data-dm-magnet-cta], [ref="addToCartButton"]');
    return element instanceof HTMLButtonElement ? element : null;
  }

  // --------------------------------------------------------------- intercepts

  /**
   * AddToCartComponent plays its "added" burst on click, once the form is valid. The upload has
   * not started yet, so that promise is held (its puppet flag) and played after the cart answers.
   * @param {Event} event
   */
  #onWindowClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button');
    if (!button || button.form !== this.#form || button.type !== 'submit') return;
    if (button.matches('[ref="addToCartButton"]')) button.dataset.puppet = 'true';
  };

  /** @param {Event} event */
  #onWindowSubmit = (event) => {
    if (event.target !== this.#form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#submit();
  };

  // ------------------------------------------------------------------- events

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target !== this.#name) return;
    this.#clearAdded();
    if (this.#name?.value.trim()) this.#setError('name', '');
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target === this.#file) {
      this.#clearAdded();
      this.#onPhotoPicked();
    } else if (event.target === this.#consent) {
      this.#clearAdded();
      if (this.#consent?.checked) this.#setError('consent', '');
    }
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-dm-magnet-change]')) this.#file?.click();
  };

  // -------------------------------------------------------------------- photo

  #onPhotoPicked() {
    const input = this.#file;
    if (!input) return;

    const file = input.files?.[0];

    // The picker was cancelled. Some browsers empty the input then; the kept photo goes back.
    if (!file) {
      this.#restorePhoto();
      this.#renderPhoto();
      return;
    }

    const problem = this.#photoProblem(file);
    if (problem) {
      this.#restorePhoto();
      this.#renderPhoto();
      this.#setError('photo', problem);
      return;
    }

    this.#photo = file;
    this.#setError('photo', '');
    this.#renderPhoto();
  }

  /**
   * @param {File} file
   * @returns {string} what is wrong with it, or nothing
   */
  #photoProblem(file) {
    if (!PHOTO_TYPES.includes(file.type) && !PHOTO_NAME.test(file.name)) return WRONG_TYPE_MESSAGE;
    if (file.size > MAX_PHOTO_BYTES) return TOO_LARGE_MESSAGE;
    return '';
  }

  /** Put the kept photo back in the input, or empty it so a rejected file can never post. */
  #restorePhoto() {
    const input = this.#file;
    if (!input) return;

    if (this.#photo) {
      try {
        const transfer = new DataTransfer();
        transfer.items.add(this.#photo);
        input.files = transfer.files;
        return;
      } catch {
        // No way to refill the input here. The tile then honestly returns to empty.
        this.#photo = null;
      }
    }
    input.value = '';
  }

  #renderPhoto() {
    const input = this.#file;
    const empty = this.querySelector('[data-dm-magnet-empty]');
    const done = this.querySelector('[data-dm-magnet-done]');
    if (!input || !(empty instanceof HTMLElement) || !(done instanceof HTMLElement)) return;

    const hadFocus = document.activeElement === input;
    const photo = this.#photo;

    empty.hidden = Boolean(photo);
    done.hidden = !photo;

    if (!photo) {
      input.removeAttribute('tabindex');
      this.#revokeThumb();
      return;
    }

    // "Change" is the way in from here, so the invisible input leaves the tab order.
    input.tabIndex = -1;

    const filename = this.querySelector('[data-dm-magnet-filename]');
    if (filename) filename.textContent = photo.name;

    this.#showThumb(photo);

    if (hadFocus) {
      const change = this.querySelector('[data-dm-magnet-change]');
      if (change instanceof HTMLElement) change.focus({ preventScroll: true });
    }
  }

  /** @param {File} photo */
  #showThumb(photo) {
    const thumb = this.querySelector('[data-dm-magnet-thumb]');
    if (!(thumb instanceof HTMLImageElement)) return;

    this.#revokeThumb();
    const url = URL.createObjectURL(photo);
    this.#thumbUrl = url;

    // The decoded image stays on screen after the object URL is released.
    thumb.onload = () => this.#revokeThumb();
    thumb.onerror = () => {
      this.#revokeThumb();
      thumb.removeAttribute('src');
    };
    thumb.src = url;
  }

  #revokeThumb() {
    if (!this.#thumbUrl) return;
    URL.revokeObjectURL(this.#thumbUrl);
    this.#thumbUrl = '';
  }

  // --------------------------------------------------------------- validation

  /**
   * @param {string} key
   * @param {string} message nothing clears it
   */
  #setError(key, message) {
    const note = this.querySelector(`[data-dm-magnet-error="${key}"]`);
    if (note instanceof HTMLElement) {
      if (message) note.textContent = message;
      note.hidden = !message;
    }

    const controls = { name: this.#name, photo: this.#file, consent: this.#consent };
    const control = controls[/** @type {'name' | 'photo' | 'consent'} */ (key)];
    if (message) control?.setAttribute('aria-invalid', 'true');
    else control?.removeAttribute('aria-invalid');
  }

  /** @returns {HTMLInputElement | null} the first control with a problem */
  #validate() {
    const name = this.#name;
    const file = this.#file;
    const consent = this.#consent;

    const nameOk = Boolean(name?.value.trim());
    const photoOk = Boolean(file?.files?.length);
    const consentOk = Boolean(consent?.checked);

    this.#setError('name', nameOk ? '' : this.#messages.name || '');
    this.#setError('photo', photoOk ? '' : this.#messages.photo || '');
    this.#setError('consent', consentOk ? '' : this.#messages.consent || '');

    if (!nameOk) return name;
    if (!photoOk) return file;
    if (!consentOk) return consent;
    return null;
  }

  /** @param {HTMLInputElement} control */
  #focusProblem(control) {
    control.focus({ preventScroll: true });

    // The file input is 1px; the tile is what the customer needs to see.
    const visible = control === this.#file ? this.querySelector('.dm-magnet__photo') : control;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    visible?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  }

  // --------------------------------------------------------------------- cart

  async #submit() {
    if (this.#submitting || !this.#form) return;

    this.#setCartError('');

    const problem = this.#validate();
    if (problem) {
      this.#focusProblem(problem);
      return;
    }

    this.#setBusy(true);

    try {
      const sections = this.#cartSections();
      const payload = await this.#postMainLine(sections);

      if (payload.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));
        this.#setCartError(payload.description || payload.message || CART_ERROR_FALLBACK);
        return;
      }

      this.#onAdded(1, payload.sections);
    } catch (error) {
      console.error('[dm-magnet]', error);
      this.#setCartError(CART_ERROR_FALLBACK);
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string[]} sections */
  async #postMainLine(sections) {
    const form = /** @type {HTMLFormElement} */ (this.#form);
    const data = new FormData(form);

    data.set('properties[Name]', this.#name?.value.trim() || '');

    if (sections.length) {
      data.set('sections', sections.join(','));
      // Render them in THIS page's context, as dm-standard-form does: otherwise Shopify renders
      // them as the home page and the header gains the index-only <h1>.
      data.set('sections_url', window.location.pathname);
    }

    const response = await fetch(this.#addUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data,
    });
    return response.json();
  }

  #addUrl() {
    return window.Theme?.routes?.cart_add_url || '/cart/add.js';
  }

  #cartSections() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId || '')
      .filter(Boolean);
  }

  /**
   * The source names the native product form on purpose: the cart bubble adds itemCount for that
   * source.
   * @param {number} itemCount
   * @param {Record<string, string> | undefined} sections
   */
  #onAdded(itemCount, sections) {
    const variantId = this.#form?.querySelector('input[name="id"]');

    this.dispatchEvent(
      new CartAddEvent({}, variantId instanceof HTMLInputElement ? variantId.value : '', {
        source: 'product-form-component',
        itemCount,
        productId: this.dataset.productId,
        sections,
      })
    );
    this.dispatchEvent(new CustomEvent('dm:cart-added', { bubbles: true, composed: true }));

    const status = this.#form?.querySelector('[data-dm-magnet-status]');
    if (status) status.textContent = ADDED_LABEL;

    this.#showAdded();
  }

  #showAdded() {
    const cta = this.#cta;
    if (!cta) return;

    // The theme's button plays the burst it was holding (see #onWindowClick).
    if (!cta.matches('[data-dm-magnet-cta]')) {
      /** @type {any} */ (cta.closest('add-to-cart-component'))?.animateAddToCart?.();
      return;
    }

    cta.dataset.label = cta.dataset.label || cta.textContent?.trim() || '';
    cta.dataset.state = 'added';
    cta.disabled = true;
    cta.textContent = ADDED_LABEL;

    window.clearTimeout(this.#addedTimer);
    this.#addedTimer = window.setTimeout(() => this.#clearAdded(), ADDED_HOLD_MS);
  }

  #clearAdded() {
    window.clearTimeout(this.#addedTimer);

    const cta = this.#cta;
    if (!cta || cta.dataset.state !== 'added') return;

    delete cta.dataset.state;
    cta.disabled = false;
    cta.textContent = cta.dataset.label || '';
  }

  /** @param {boolean} on */
  #setBusy(on) {
    this.#submitting = on;
    if (on) this.#clearAdded();
    this.#cta?.setAttribute('aria-busy', String(on));
  }

  /** @param {string} message nothing clears it */
  #setCartError(message) {
    const note = this.#form?.querySelector('[data-dm-magnet-cart-error]');
    if (!(note instanceof HTMLElement)) return;

    note.textContent = message;
    note.hidden = !message;
  }
}

if (!customElements.get('dm-magnet-fields')) {
  customElements.define('dm-magnet-fields', DmMagnetFields);
}
